import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Stack,
  Divider,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PageTitle from "@Components/PageTitle/PageTitle";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import themeCreator from "@Themes/themeCreator";
import {
  StyledCard,
  SectionTitle,
  ValueText,
} from "@Pages/RegulatoryMember/ExitRequestDetail/styles";
import DelegationRequestPopup from "@Components/DelegationRequestPopup/DelegationRequestPopup";
import ExtensionRequestDialog from "@Components/ExtensionRequestDialog/ExtensionRequestDialog";
import ExtensionStatusBanner from "@Components/ExtensionRequestDialog/ExtensionStatusBanner";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import axiosInstance from "@API/axios";
import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";

const ExitRequestDetailRD = () => {
  const { reqId, categoryAssignmentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const requestDataFromRedux = useSelector(
    (state) => state.requestData.requestData
  );
  const userRegions = useSelector((state) => state.user.regions || []);
  const userRole = useSelector((state) => state.user.role);

  // State for the request details
  const [requestData, setRequestData] = useState(
    requestDataFromRedux?.exitRequest || requestDataFromRedux || null
  );
  const [loading, setLoading] = useState(!requestDataFromRedux);
  const [delegationPopupOpen, setDelegationPopupOpen] = useState(false);
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState(
    requestDataFromRedux?.categoryDescription || null
  );

  // Data fetching
  useEffect(() => {
    console.log("Request Data From Redux:", requestDataFromRedux);
    setLoading(true);
    axiosInstance
      .get(
        `/exit-request-response/my-submissions/${reqId}/${categoryAssignmentId}`
      )
      .then((res) => {
        setRequestData(res.data.exitRequest);
        setCategoryDescription(res.data.categoryDescription || null);
        console.log("Fetched Request Data:", res.data);
        if (
          res.data.categoryAssignmentId == null &&
          res.data.exitRequest.categoryAssignmentId == null
        ) {
          setNotFound(true);
          dispatch(
            setSnackbar({
              open: true,
              message: "Request not found or category assignment is missing.",
              error: true,
            })
          );
          setLoading(false);
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
        dispatch(
          setSnackbar({
            open: true,
            message: "Failed to fetch exit request details.",
            error: true,
          })
        );
      });
  }, [reqId, requestDataFromRedux]);

  const handleBackClick = () => navigate(-1);

  const handleCloneClick = async () => {
    try {
      const response = await axiosInstance.get(`/exitRequest/clone/${reqId}`);
      if (response.data) {
        navigate(DefaultRoutes.createIngredientExit, {
          state: {
            cloneData: response.data,
            clonedFrom: reqId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching copy data:", error);
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to load exit request data for copying.",
          error: true,
        })
      );
    }
  };

  const isFromIncomingRequests = location.pathname.includes(
    "rd-incoming-requests"
  );
  const isFromEscalatedRequests = location.pathname.includes(
    "escalated-requests"
  );
  const isFromRdManagerIncoming = location.pathname.includes(
    "rdm-incoming-requests"
  );
  const handleRespondClick = () => {
    if (isFromEscalatedRequests) {
      navigate(
        DefaultRoutes.ApproverEscalatedRequestResponse
          .replace(":reqId", reqId)
          .replace(":categoryAssignmentId", categoryAssignmentId)
      );
    } else if (isFromRdManagerIncoming) {
      navigate(
        DefaultRoutes.rdManagerRequestResponseFromIncomingRequests
          .replace(":reqId", reqId)
          .replace(":categoryAssignmentId", categoryAssignmentId)
      );
    } else if (isFromIncomingRequests) {
      navigate(
        DefaultRoutes.rdRequestResponseFromIncomingRequests
          .replace(":reqId", reqId)
          .replace(":categoryAssignmentId", categoryAssignmentId)
      );
    }
  };
  const handleDelegateClick = (requestId, categoryAssignmentId) => {
    setDelegationPopupOpen(true);
  };

  const visibleCountries = useMemo(() => {
    const countries = requestData?.countries || [];
    if (!countries.length) return [];
    if (!userRegions.length) return countries;

    const normalizedRegions = userRegions.map((region) =>
      (region.name || region.regionName || region)?.toLowerCase()
    );

    return countries.filter((country) =>
      normalizedRegions.includes((country.regionName || "").toLowerCase())
    );
  }, [requestData?.countries, userRegions]);

  if (loading) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <LoadingComponent size={60} thickness={4} color="primary" />
      </Box>
    );
  }

  if (notFound) {
    const fallbackRoute = isFromEscalatedRequests
      ? DefaultRoutes.ApproverEscalatedRequests
      : isFromRdManagerIncoming
        ? DefaultRoutes.rdManagerIncomingRequests
        : DefaultRoutes.rdIncomingRequests;
    navigate(fallbackRoute, { replace: true });
    return null;
  }

  return (
    <Box
      sx={{ p: 4, height: "91vh", display: "flex", flexDirection: "column" }}
    >
      {/* Page Header */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <PageTitle
          title="Exit Request Details"
          PageIcon={<InfoIcon color="white" fontSize="large" />}
          iconColor={themeCreator().palette.primary.main}
          noMargin
        />
        <Box sx={{ display: "flex", gap: 1 }}>
          {userRole === "REGULATORY" && (
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleCloneClick}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
                "&:hover": {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.contrastText,
                },
              }}
              color="primary"
            >
              Copy
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                backgroundColor: "#ffffff",
                color: theme.palette.primary.main,
                boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
              },
            }}
          >
            Return
          </Button>
        </Box>
      </Box>

      {/* Request Details Section */}
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <StyledCard
              sx={{
                transition: "0.3s",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                },
                height: "100%",
                backgroundColor: "#fcfcfc",
                borderLeft: `4px solid ${theme.palette.primary.main}`,
              }}
            >
              <CardContent>
                <SectionTitle
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: "1rem",
                    borderBottomWidth: "2px",
                    borderBottomColor: theme.palette.grey.light2,
                    pb: 0.5,
                    mb: 1.5,
                  }}
                >
                  Ingredient Details
                </SectionTitle>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  {/* Left side of the card - ingredient basic info */}
                  <Grid container spacing={2} sx={{ width: "50%", pr: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Name
                      </Typography>
                      <ValueText variant="body1">
                        {requestData.ingredient.name}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        CAS Number
                      </Typography>
                      <ValueText variant="body1">
                        {requestData.ingredient.casNumber}
                      </ValueText>
                    </Grid>
                  </Grid>

                  {/* Right side of the card - description */}
                  <Box
                    sx={{
                      width: "50%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Description
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: theme.palette.grey[50],
                        height: "100%",
                        maxHeight: "120px",
                        overflow: "auto",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {requestData.ingredient.description}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard
              sx={{
                transition: "0.3s",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                },
                height: "100%",
                backgroundColor: "#fcfcfc",
              }}
            >
              <CardContent>
                <SectionTitle
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: "1rem",
                    borderBottomWidth: "2px",
                    borderBottomColor: theme.palette.grey.light2,
                    pb: 0.5,
                    mb: 1.5,
                  }}
                >
                  Restriction Details
                </SectionTitle>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Grid container spacing={2} sx={{ width: "50%", pr: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Restriction Type
                      </Typography>
                      <Chip
                        label={
                          requestData.restrictionType === "BANNED"
                            ? "BANNED"
                            : requestData.restrictionType ===
                              "LABELING_REQUIREMENTS"
                            ? "LABELING REQUIREMENTS"
                            : requestData.restrictionType ===
                              "RESTRICTED_AND_SDS"
                            ? "RESTRICTED AND SDS"
                            : "RESTRICTED"
                        }
                        color={
                          requestData.restrictionType === "BANNED"
                            ? "error"
                            : "warning"
                        }
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                          {requestData.primaryDateType ? requestData.primaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Primary Date"}
                      </Typography>
                      <ValueText variant="body1" color="error">
                        {requestData.primaryDate
                          ? new Date(requestData.primaryDate).toLocaleDateString()
                          : "-"}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {requestData.secondaryDateType ? requestData.secondaryDateType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Secondary Date"}
                      </Typography>
                      <ValueText variant="body1" sx={{ color: theme.palette.warning.main }}>
                        {requestData.secondaryDate
                          ? new Date(requestData.secondaryDate).toLocaleDateString()
                          : "-"}
                      </ValueText>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        UL Internal Date
                      </Typography>
                      <ValueText variant="body1" sx={{ color: theme.palette.info.main }}>
                        {requestData.ulInternalDate
                          ? new Date(requestData.ulInternalDate).toLocaleDateString()
                          : "-"}
                      </ValueText>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      width: "50%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      Additional Info
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: theme.palette.grey[50],
                        height: "100%",
                        maxHeight: "120px",
                        overflow: "auto",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                 {requestData.additionalInfo && requestData.additionalInfo.trim() !== ""
                   ? requestData.additionalInfo
                   : "No additional information provided."}
                      </Typography>
                    </Paper>
                  </Box>
                </Box>
                {categoryDescription && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Restrictions Descriptions
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: theme.palette.grey[50],
                        maxHeight: "80px",
                        overflow: "auto",
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {categoryDescription}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </CardContent>
            </StyledCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledCard
              sx={{
                transition: "0.3s",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                },
                height: "100%",
                backgroundColor: "#fcfcfc",
              }}
            >
              <CardContent>
                <SectionTitle
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontSize: "1rem",
                    borderBottomWidth: "2px",
                    borderBottomColor: theme.palette.grey.light2,
                    pb: 0.5,
                    mb: 1.5,
                  }}
                >
                  Request Information
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Created By
                    </Typography>
                    <ValueText variant="body1">
                      {requestData.user.username}
                    </ValueText>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Role
                    </Typography>
                    <ValueText variant="body1">
                      {requestData.user.role}
                    </ValueText>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Created On
                    </Typography>
                    <ValueText variant="body1">
                      {requestData.createdAt
                        ? new Date(requestData.createdAt).toLocaleDateString()
                        : "-"}
                    </ValueText>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Category
                    </Typography>
                    <ValueText variant="body1">
                      {requestData.categoryName}
                    </ValueText>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Contact Email
                    </Typography>
                    <ValueText variant="body1">
                      <a
                        href={`mailto:${requestData.user.email}`}
                        style={{ color: theme.palette.primary.main }}
                      >
                        {requestData.user.email}
                      </a>
                    </ValueText>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Countries
                    </Typography>
                    {visibleCountries.length ? (
                      <Stack direction="row" spacing={0.75} flexWrap="wrap">
                        {visibleCountries.map((country) => (
                          <Chip
                            key={country.id || country.countryCode}
                            label={`${country.name}`}
                            size="small"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <ValueText variant="body1">
                        No assigned countries for your regions
                      </ValueText>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={12}>
            <ExtensionStatusBanner categoryAssignmentId={categoryAssignmentId} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: "auto",
                pt: 2,
                pr: 1,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => setExtensionDialogOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#ffffff",
                    color: theme.palette.primary.main,
                    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                  },
                }}
              >
                Request Extension
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="large"
                onClick={() => handleDelegateClick(reqId, categoryAssignmentId)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#ffffff",
                    color: theme.palette.primary.main,
                    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                  },
                }}
              >
                Request Delegation
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={handleRespondClick}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 3,
                  "&:hover": {
                    backgroundColor: "#ffffff",
                    color: theme.palette.primary.main,
                    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
                  },
                }}
              >
                Respond to Request
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {delegationPopupOpen && (
        <DelegationRequestPopup
          open={delegationPopupOpen}
          onClose={() => setDelegationPopupOpen(false)}
          requestId={reqId}
          categoryAssignmentId={categoryAssignmentId}
        />
      )}
      {extensionDialogOpen && (
        <ExtensionRequestDialog
          open={extensionDialogOpen}
          onClose={() => setExtensionDialogOpen(false)}
          exitRequestId={Number(reqId)}
          categoryAssignmentId={Number(categoryAssignmentId)}
          onSubmitted={() =>
            dispatch(
              setSnackbar({
                open: true,
                message: "Extension request submitted.",
                error: false,
              })
            )
          }
        />
      )}
    </Box>
  );
};

export default ExitRequestDetailRD;
